<template>
  <ContentWrap>
    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      show-icon
      title="团队奖励（Brokerage 收敛）"
      description="按直推一级人数阶梯发放固定佣金，入 trade_brokerage_record（业务类型=团队奖励），走现有冻结/提现。不新建分红池；与订单推广佣金可并存。"
    />
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="120px"
    >
      <el-form-item label="启用" prop="enabled">
        <el-switch v-model="formData.enabled" />
        <el-text class="w-full" size="small" type="info">
          关闭后停止新发；已入账不回滚
        </el-text>
      </el-form-item>
      <el-form-item label="奖励阶梯" prop="tiers">
        <el-table :data="formData.tiers" border class="mb-12px !w-720px">
          <el-table-column label="直推人数 ≥" min-width="160">
            <template #default="{ row }">
              <el-input-number v-model="row.directCount" :min="1" :precision="0" class="!w-140px" />
            </template>
          </el-table-column>
          <el-table-column label="奖励金额（元）" min-width="180">
            <template #default="{ row }">
              <el-input-number
                v-model="row.rewardYuan"
                :min="0.01"
                :precision="2"
                :step="1"
                class="!w-160px"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100" align="center">
            <template #default="{ $index }">
              <el-button link type="danger" @click="removeTier($index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-button type="primary" plain @click="addTier">添加阶梯</el-button>
        <el-text class="w-full" size="small" type="info">
          口径与 C 端「我的团队」一级人数一致（bind_user_id）；每档达标只发一次；冻结天数同交易配置
        </el-text>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="formLoading" @click="submitForm">保 存</el-button>
        <el-button
          type="warning"
          plain
          :loading="settleLoading"
          @click="handleSettle"
        >
          立即发放扫描
        </el-button>
      </el-form-item>
    </el-form>

    <el-divider content-position="left">试算（不入账）</el-divider>
    <el-form inline label-width="100px" class="mb-12px">
      <el-form-item label="用户">
        <el-select v-model="previewUserId" class="!w-180px" filterable clearable placeholder="请选择用户">
          <el-option v-for="u in staffOptions" :key="u.value" :label="u.label" :value="u.value" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" plain :loading="previewLoading" @click="handlePreview">
          试算
        </el-button>
      </el-form-item>
    </el-form>
    <el-descriptions v-if="previewResult" :column="3" border class="mb-12px !w-720px">
      <el-descriptions-item label="用户">{{ previewResult.userId }}</el-descriptions-item>
      <el-descriptions-item label="规则启用">
        {{ previewResult.enabled ? '是' : '否' }}
      </el-descriptions-item>
      <el-descriptions-item label="直推一级">{{ previewResult.directCount }}</el-descriptions-item>
    </el-descriptions>
    <el-table
      v-if="previewResult?.tiers?.length"
      :data="previewResult.tiers"
      border
      class="!w-820px"
    >
      <el-table-column label="门槛 ≥" prop="directCount" width="100" />
      <el-table-column label="奖励(元)" width="120">
        <template #default="{ row }">{{ ((row.rewardPrice || 0) / 100).toFixed(2) }}</template>
      </el-table-column>
      <el-table-column label="已达标" width="90">
        <template #default="{ row }">
          <el-tag :type="row.reached ? 'success' : 'info'" size="small">
            {{ row.reached ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="已发放" width="90">
        <template #default="{ row }">
          <el-tag :type="row.granted ? 'warning' : 'info'" size="small">
            {{ row.granted ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="扫描会新发" min-width="120">
        <template #default="{ row }">
          <el-tag :type="row.wouldGrantNow ? 'danger' : 'info'" size="small">
            {{ row.wouldGrantNow ? '会' : '不会' }}
          </el-tag>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>
</template>

<script lang="ts" setup>
import * as TeamRewardApi from '@/api/mall/trade/brokerage/teamReward'
import { cloneDeep } from 'lodash-es'

defineOptions({ name: 'TradeBrokerageTeamReward' })

const { staffOptions, staffMap, loadStaffs } = useStaff()
const message = useMessage()

interface TierRow {
  directCount: number
  rewardYuan: number
}

const formLoading = ref(false)
const settleLoading = ref(false)
const previewLoading = ref(false)
const previewUserId = ref<number>()
const previewResult = ref<TeamRewardApi.TeamRewardPreviewVO>()
const formRef = ref()
const formData = ref<{ enabled: boolean; tiers: TierRow[] }>({
  enabled: false,
  tiers: []
})

const formRules = reactive({
  tiers: [
    {
      validator: (_: any, __: any, callback: (e?: Error) => void) => {
        if (!formData.value.tiers?.length) {
          callback(new Error('请至少配置一档阶梯'))
          return
        }
        callback()
      },
      trigger: 'change'
    }
  ]
})

const addTier = () => {
  formData.value.tiers.push({ directCount: 3, rewardYuan: 10 })
}

const removeTier = (index: number) => {
  formData.value.tiers.splice(index, 1)
}

const loadConfig = async () => {
  formLoading.value = true
  try {
    const data = await TeamRewardApi.getTeamRewardConfig()
    formData.value.enabled = !!data?.enabled
    formData.value.tiers = (data?.tiers || []).map((t) => ({
      directCount: t.directCount,
      rewardYuan: (t.rewardPrice || 0) / 100
    }))
    if (!formData.value.tiers.length) {
      formData.value.tiers = [
        { directCount: 3, rewardYuan: 10 },
        { directCount: 10, rewardYuan: 50 }
      ]
    }
  } finally {
    formLoading.value = false
  }
}

const submitForm = async () => {
  if (!formRef.value) return
  await formRef.value.validate()
  formLoading.value = true
  try {
    const payload: TeamRewardApi.TeamRewardConfigVO = {
      enabled: formData.value.enabled,
      tiers: cloneDeep(formData.value.tiers).map((t) => ({
        directCount: t.directCount,
        rewardPrice: Math.round(t.rewardYuan * 100)
      }))
    }
    await TeamRewardApi.saveTeamRewardConfig(payload)
    message.success('保存成功')
    await loadConfig()
  } finally {
    formLoading.value = false
  }
}

const handleSettle = async () => {
  settleLoading.value = true
  try {
    const count = await TeamRewardApi.settleTeamReward()
    message.success(`扫描完成，新入账 ${count ?? 0} 笔`)
  } finally {
    settleLoading.value = false
  }
}

const handlePreview = async () => {
  if (!previewUserId.value) {
    message.warning('请输入用户编号')
    return
  }
  previewLoading.value = true
  try {
    previewResult.value = await TeamRewardApi.previewTeamReward(previewUserId.value)
  } finally {
    previewLoading.value = false
  }
}

onMounted(() => { loadStaffs(); loadConfig() })
</script>
